/**
 * Consumer Notifications Inbox Page
 *
 * Full-page notification inbox with tabs (All/Unread/Read), search,
 * and mark-all-read functionality.
 *
 * @see Issue #699 - Notification System Fidelity
 */

import NotificationsIcon from '@mui/icons-material/Notifications';
import { NotificationInbox, type NotificationInboxTab, type NotificationItemData } from '@ops-matrix/design-system-ui';
import { isSafeActionUrl } from '@ops-matrix/shared-types';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { useMarkAllRead, useMarkAsRead, useMarkAsUnread, useNotifications } from '../../hooks/useNotifications';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<NotificationInboxTab>('all');
  const [page, setPage] = useState(1);
  const limit = 20;

  const statusFilter = activeTab === 'all' ? ('all' as const) : activeTab;

  const { data, isLoading } = useNotifications({
    page,
    limit,
    status: statusFilter,
  });

  const markAsReadMutation = useMarkAsRead();
  const markAsUnreadMutation = useMarkAsUnread();
  const markAllReadMutation = useMarkAllRead();

  const notifications: NotificationItemData[] = data?.data ?? [];
  const hasMore = (data?.page ?? 1) < (data?.totalPages ?? 1);

  const handleTabChange = useCallback((tab: NotificationInboxTab) => {
    setActiveTab(tab);
    setPage(1);
  }, []);

  const handleLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const handleMarkAllRead = useCallback(() => {
    markAllReadMutation.mutate();
  }, [markAllReadMutation]);

  const handleNotificationClick = useCallback(
    (notification: NotificationItemData) => {
      if (!notification.isRead) {
        markAsReadMutation.mutate({ notificationId: notification.id });
      }
      const actionUrl = notification.actionUrl;
      if (actionUrl) {
        // Audit §3.1 — never follow an untrusted actionUrl (defense-in-depth
        // for legacy rows predating service-layer validation).
        const target = isSafeActionUrl(actionUrl) ? actionUrl : '/notifications';
        if (target !== actionUrl) {
          // Deliberately NOT logging notification.id or the url — both would
          // leak sensitive data into devtools.
          // biome-ignore lint/suspicious/noConsole: surface in browser telemetry
          console.warn('[notification] blocked unsafe actionUrl');
        }
        setTimeout(() => navigate(target), 0);
      }
    },
    [markAsReadMutation, navigate],
  );

  const handleToggleRead = useCallback(
    (notification: NotificationItemData) => {
      if (notification.isRead) {
        markAsUnreadMutation.mutate({ notificationId: notification.id });
      } else {
        markAsReadMutation.mutate({ notificationId: notification.id });
      }
    },
    [markAsReadMutation, markAsUnreadMutation],
  );

  return (
    <PageTemplate
      variant="list"
      titleKey="notification.inbox.title"
      descriptionKey="notification.inbox.description"
      icon={NotificationsIcon}
    >
      <NotificationInbox
        notifications={notifications}
        loading={isLoading}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onMarkAllRead={handleMarkAllRead}
        onNotificationClick={handleNotificationClick}
        onToggleRead={handleToggleRead}
        onTabChange={handleTabChange}
        activeTab={activeTab}
      />
    </PageTemplate>
  );
}
